import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Alert,
  Snackbar,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AuthenticationService from "../../AuthenticationService";
import { useMaterialUIController } from "context";

function CTLPassword({ open: controlledOpen, onClose }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // If no open/onClose props, behave as a standalone page (always open, no close)
  const isDialog = controlledOpen !== undefined;
  const isOpen = isDialog ? controlledOpen : true;

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const resetForm = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleClose = () => {
    resetForm();
    if (onClose) onClose();
  };

  const passwordValidation = (password) => {
    return (
      /.{8,}/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password) &&
      /[A-Z]/.test(password) &&
      /\d/.test(password)
    );
  };

  const isNewPasswordValid =
    AuthenticationService.getPasswordComplexity() === "true" ||
    AuthenticationService.getPasswordComplexity() === true
      ? passwordValidation(newPassword)
      : true;
  const isPasswordsMatch = newPassword === confirmPassword;
  const canSubmit = oldPassword && isNewPasswordValid && isPasswordsMatch && newPassword && confirmPassword;

  const handleApply = () => {
    const username = AuthenticationService.getUserName();
    const data = {
      username,
      oldPassword,
      newPassword,
      confirmPassword,
    };

    AuthenticationService.ChangePassword(data)
      .then(() => {
        setSnackbar({ open: true, message: "Password changed successfully! Please log in again.", severity: "success" });
        resetForm();
        if (onClose) onClose();
        setTimeout(() => {
          AuthenticationService.logout();
          window.location.href = "/site/log-out";
        }, 1500);
      })
      .catch((error) => {
        const msg = error.response?.data?.message
          ? `${error.response.data.message} ${error.response.data.messageDetail || ""}`
          : error.message || "Failed to change password.";
        setSnackbar({ open: true, message: msg, severity: "error" });
      });
  };

  const paperBg = darkMode ? "#1e1e1e" : "#ffffff";
  const textColor = darkMode ? "#ffffff" : "rgba(0, 0, 0, 0.87)";
  const labelColor = darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)";
  const borderColor = darkMode ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)";
  const iconColor = darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)";

  const textFieldSx = {
    mb: 1.5,
    "& .MuiOutlinedInput-root": {
      color: textColor,
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.87)" },
      "&.Mui-focused fieldset": { borderColor: darkMode ? "#90caf9" : "#1976d2" },
    },
    "& .MuiInputLabel-root": { color: labelColor },
    "& .MuiInputLabel-root.Mui-focused": { color: darkMode ? "#90caf9" : "#1976d2" },
  };

  const passwordField = (label, value, onChange, show, setShow) => (
    <TextField
      type={show ? "text" : "password"}
      label={label}
      variant="outlined"
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="off"
      sx={textFieldSx}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onMouseDown={() => setShow(true)}
              onMouseUp={() => setShow(false)}
              sx={{ color: iconColor }}
            >
              {show ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={isDialog ? handleClose : undefined}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={!isDialog}
        PaperProps={{
          sx: {
            bgcolor: paperBg,
            color: textColor,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ color: textColor }}>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {passwordField("Old Password", oldPassword, setOldPassword, showOldPassword, setShowOldPassword)}
            {passwordField("New Password", newPassword, setNewPassword, showNewPassword, setShowNewPassword)}
            {!isNewPasswordValid && newPassword && (
              <Typography variant="caption" color="error" sx={{ mb: 1, display: "block" }}>
                Password must be at least 8 characters, contain a special character, a capital letter, and a number.
              </Typography>
            )}
            {passwordField("Confirm New Password", confirmPassword, setConfirmPassword, showConfirmPassword, setShowConfirmPassword)}
            {!isPasswordsMatch && confirmPassword && (
              <Typography variant="caption" color="error">
                Passwords do not match.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {isDialog && (
            <Button onClick={handleClose} sx={{ color: darkMode ? "rgba(255,255,255,0.7)" : undefined }}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!canSubmit}
            sx={{
              bgcolor: darkMode ? "#66bb6a" : "#2e7d32",
              color: "#fff",
              "&:hover": { bgcolor: darkMode ? "#57a05a" : "#1b5e20" },
              "&.Mui-disabled": {
                bgcolor: darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
                color: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.26)",
              },
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CTLPassword;
